import { useCallback, useEffect, useMemo, useRef, useState } from 'react'

import { Box } from '@factorialco/deprecated-design-system'
import {
  Background,
  type Connection,
  type EdgeChange,
  type NodeChange,
  ReactFlow,
  ReactFlowProvider,
  addEdge,
  applyEdgeChanges,
  applyNodeChanges,
  getOutgoers,
  useReactFlow,
} from '@xyflow/react'
import '@xyflow/react/dist/style.css'
import { SaveWorkflowGraphDocument, WorkflowContainerFragment } from 'generated/resources/graphql'

import { useMutation } from 'modules/core/hooks/useMutation'
import { handleResult } from 'modules/core/lib/handleMutation'
import i18n from 'modules/core/lib/i18n'
import app from 'modules/core/store/models/app'

import { VariablesConfig } from 'modules/workflows/components//Sidepanel/VariableSelectorTypes'
import {
  EntityResolution,
  HandlersConfig,
  TriggerConfig,
  WorkflowContext,
} from 'modules/workflows/contexts/WorkflowContext'
import { useWorkflowUpdate } from 'modules/workflows/contexts/WorkflowProvider'
import 'modules/workflows/css/style.css'
import {
  WorkflowNavConfirmModal,
  useWorkflowNavConfirmModal,
} from 'modules/workflows/hooks/useWorkflowNavConfirmModal'
import { WorkflowLayoutContext } from 'modules/workflows/lib/WorkflowLayoutContext'
import { extractEntityIdsFromWorkflow } from 'modules/workflows/lib/extractEntityIds'
import {
  mapGraphQLToUI,
  serializeEdgesForMutation,
  serializeNodesForMutation,
} from 'modules/workflows/lib/flow/adapters'
import { useDagreAutoLayout } from 'modules/workflows/lib/useDagreAutoLayout'
import { getBuildingBlocks } from 'modules/workflows/provider/buildingBlocks'

import { ConnectionLine } from './Edge/ConnectionLine'
import { edgeTypes } from './Edge/edgeTypes'
import { nodeTypes } from './Node/nodeTypes'
import { updateNodesWithValidationAndSteps } from './Node/nodeUpdateUtils'
import { EdgeUI, NodeUI, isNodeUICondition, isNodeUIConditionIfOrElse } from './Node/types'
import { Sidepanel } from './Sidepanel/Sidepanel'
import { type BuildingBlock } from './WorkflowBuildingBlocksBox'
import { WorkflowZoom } from './WorkflowZoom'

type NodeDecorator = (node: NodeUI) => React.ReactNode | undefined

/**
 * Hook type for resolving entity IDs to names.
 * Product code implements this hook with actual GraphQL queries.
 * The hook receives entity IDs extracted from workflow nodes and returns a resolution map.
 */
export type UseEntityResolution = (entityIds: Record<string, string[]>) => EntityResolution

type Props = {
  workflow: WorkflowContainerFragment
  nodeDecorator?: NodeDecorator
  variables: VariablesConfig
  trigger: TriggerConfig
  handlers: HandlersConfig
  heightOffset?: string
  /**
   * Optional hook for resolving entity IDs to display names.
   * Product code provides this hook with actual data fetching logic.
   * The hook is called with entity IDs extracted from workflow nodes
   * and should return an EntityResolution map.
   */
  useEntityResolution?: UseEntityResolution
  /**
   * @internal
   * Test-only mode that disables d3-drag features incompatible with JSDOM.
   * Should only be used in Jest tests. Do not use in production code.
   */
  testMode?: boolean
}

// Default no-op hook that returns empty resolution
const defaultUseEntityResolution: UseEntityResolution = () => ({})

type WorkflowContentInnerProps = Omit<Props, 'workflow'> & {
  workflow: WorkflowContainerFragment
  workflowManifest: NonNullable<WorkflowContainerFragment['workflowManifest']>
}

const WorkflowContentInner = ({
  workflow,
  workflowManifest,
  nodeDecorator,
  variables,
  handlers,
  trigger,
  heightOffset = '0px',
  useEntityResolution = defaultUseEntityResolution,
  testMode = false,
}: WorkflowContentInnerProps) => {
  const { getNodes, getEdges } = useReactFlow<NodeUI, EdgeUI>()

  const [sidepanelDirty, setSidepanelDirty] = useState(false)

  const [pendingSidePanelChangesModal, setPendingSidePanelChangesModal] = useState<
    NodeChange<NodeUI>[] | null
  >(null)

  const discardUnsavedSidePanelChanges = () => {
    setSidepanelDirty(false)
    setNavigationBlocked(false)
    setPendingSidePanelChangesModal(null)
    onNodesChange(pendingSidePanelChangesModal ?? [], false)
  }

  const continueEditingSidePanel = () => {
    setPendingSidePanelChangesModal(null)
    setNavigationBlocked(false)
  }

  const buildingBlocks: BuildingBlock[] = getBuildingBlocks()

  // Memoized transformation of server data to UI state
  const initialData = useMemo(() => {
    const { nodesUI, edgesUI } = mapGraphQLToUI(
      workflow.workflowNodes,
      workflow.workflowEdges,
      workflowManifest.handlers ?? [],
      nodeDecorator
    )

    return { nodesUI, edgesUI }
  }, [workflow, workflowManifest?.handlers, nodeDecorator])

  // Dirty state tracking
  const [isDirty, setIsDirty] = useState(false)

  // Base setters without dirty tracking (for internal/system updates)
  // we don't expose these settlers in the context. Instead we expose
  // specific settlers like elevateNodeZIndex/restoreNodeZIndex
  const [nodesUI, setNodesInternal] = useState<NodeUI[]>(initialData.nodesUI)
  const [edgesUI, setEdgesInternal] = useState<EdgeUI[]>(initialData.edgesUI)

  // Extract entity IDs from current nodes for resolution
  const entityIds = useMemo(
    () => extractEntityIdsFromWorkflow(nodesUI, variables),
    [nodesUI, variables]
  )

  // Resolve entity IDs to names using the product-provided hook
  const entityResolution = useEntityResolution(entityIds)

  // Public setters with dirty tracking (for user actions)
  const setNodesUI = useCallback((updater: NodeUI[] | ((prev: NodeUI[]) => NodeUI[])) => {
    setNodesInternal(updater)
    setIsDirty(true)
  }, [])

  const setEdgesUI = useCallback((updater: EdgeUI[] | ((prev: EdgeUI[]) => EdgeUI[])) => {
    setEdgesInternal(updater)
    setIsDirty(true)
  }, [])

  // Helper to sync both nodes and edges from server without marking as dirty
  // Optionally preserves the selected node state
  const syncFromServer = useCallback(
    (nodes: NodeUI[], edges: EdgeUI[], preserveSelectionId?: string) => {
      // If a selection ID is provided, preserve its selection state
      const nodesWithPreservedSelection = preserveSelectionId
        ? nodes.map((node) => ({
            ...node,
            selected: node.id === preserveSelectionId,
          }))
        : nodes

      setNodesInternal(nodesWithPreservedSelection)
      setEdgesInternal(edges)
      setIsDirty(false)
    },
    []
  )

  // Track previous nodes and edges to detect actual changes (not just reference changes)
  const prevNodesRef = useRef(workflow.workflowNodes)
  const prevEdgesRef = useRef(workflow.workflowEdges)
  const isInitialMountRef = useRef(true)

  // Sync from server when workflow nodes or edges actually change (not just on refetch)
  // This prevents breaking the graph structure when only workflowNodeFiles change
  useEffect(() => {
    // Skip on initial mount - let initialData handle the first load
    if (isInitialMountRef.current) {
      isInitialMountRef.current = false
      prevNodesRef.current = workflow.workflowNodes
      prevEdgesRef.current = workflow.workflowEdges
      return
    }

    // Skip if nodesUI is empty
    if (nodesUI.length === 0) {
      prevNodesRef.current = workflow.workflowNodes
      prevEdgesRef.current = workflow.workflowEdges
      return
    }

    // Check if nodes actually changed (compare IDs and count)
    const currentNodeIds = new Set(workflow.workflowNodes.map((n) => String(n.id)))
    const prevNodeIds = new Set(prevNodesRef.current.map((n) => String(n.id)))
    const nodesChanged =
      currentNodeIds.size !== prevNodeIds.size ||
      [...currentNodeIds].some((id) => !prevNodeIds.has(id)) ||
      [...prevNodeIds].some((id) => !currentNodeIds.has(id))

    // Check if edges actually changed (compare IDs and count)
    const currentEdgeIds = new Set(workflow.workflowEdges.map((e) => String(e.id)))
    const prevEdgeIds = new Set(prevEdgesRef.current.map((e) => String(e.id)))
    const edgesChanged =
      currentEdgeIds.size !== prevEdgeIds.size ||
      [...currentEdgeIds].some((id) => !prevEdgeIds.has(id)) ||
      [...prevEdgeIds].some((id) => !currentEdgeIds.has(id))

    // Only sync if nodes or edges actually changed
    // This prevents breaking the graph when only workflowNodeFiles change
    if (!nodesChanged && !edgesChanged) {
      return
    }

    // Get currently selected node ID to preserve selection
    const currentSelectedNodeId = nodesUI.find((node) => node.selected)?.id

    // Recalculate nodes from server data
    const { nodesUI: newNodesUI, edgesUI: newEdgesUI } = mapGraphQLToUI(
      workflow.workflowNodes,
      workflow.workflowEdges,
      workflowManifest?.handlers ?? [],
      nodeDecorator
    )

    // Merge: Keep local nodes (not in server data) and update server nodes
    // This preserves local nodes that haven't been persisted yet
    const serverNodeIds = new Set(newNodesUI.map((n) => n.id))
    const localNodes = nodesUI.filter((node) => !serverNodeIds.has(node.id))

    // Combine server nodes with local nodes
    const mergedNodes = [...newNodesUI, ...localNodes]

    // Sync with preserved selection
    syncFromServer(mergedNodes, newEdgesUI, currentSelectedNodeId)

    // Update refs for next comparison
    prevNodesRef.current = workflow.workflowNodes
    prevEdgesRef.current = workflow.workflowEdges
  }, [
    workflow.workflowNodes,
    workflow.workflowEdges,
    workflowManifest?.handlers,
    nodeDecorator,
    nodesUI,
    syncFromServer,
  ])

  // Z-index management (UI-only, no dirty tracking)
  const elevateNodeZIndex = useCallback((nodeId: string) => {
    setNodesInternal((nodes) => nodes.map((n) => (n.id === nodeId ? { ...n, zIndex: 9999 } : n)))
  }, [])

  const restoreNodeZIndex = useCallback((nodeId: string) => {
    setNodesInternal((nodes) =>
      nodes.map((n) => (n.id === nodeId ? { ...n, zIndex: undefined } : n))
    )
  }, [])
  const [newConnection, setNewConnection] = useState<Connection | undefined>(undefined)
  const [previousNodeIds, setPreviousNodeIds] = useState<Set<string>>(
    new Set(initialData.nodesUI.map((n) => n.id))
  )

  // Menu coordination - scoped to this workflow instance
  const openMenusRef = useRef<Map<string, () => void>>(new Map())

  const menuControls = useMemo(
    () => ({
      closeAllMenus: () => {
        openMenusRef.current.forEach((callback) => callback())
        openMenusRef.current.clear()
      },
      registerMenu: (id: string, closeCallback: () => void) => {
        openMenusRef.current.set(id, closeCallback)
        // Return cleanup function
        return () => {
          openMenusRef.current.delete(id)
        }
      },
    }),
    []
  )

  // Save workflow mutation
  const [saveWorkflowGraph] = useMutation(SaveWorkflowGraphDocument, {
    extractorFn: (x) => x.workflowsMutations.saveGraphWorkflowsWorkflow,
  })

  // Functions to get serialized nodes and edges from current state
  const getSerializedNodesHandler = useCallback(() => {
    const currentNodes = getNodes()
    return serializeNodesForMutation(currentNodes)
  }, [getNodes])

  const getSerializedEdgesHandler = useCallback(() => {
    const currentEdges = getEdges()
    return serializeEdgesForMutation(currentEdges, nodesUI)
  }, [getEdges, nodesUI])

  // Handle save functionality
  const handleSave = useCallback(async () => {
    try {
      // Convert UI nodes and edges to GraphQL input format using the same handlers
      const nodes = getSerializedNodesHandler()
      const edges = getSerializedEdgesHandler()

      const result = await saveWorkflowGraph({
        variables: {
          companyId: workflow.companyId,
          workflowId: workflow.id,
          nodes,
          edges,
        },
      })

      const handled = handleResult(result, (errors) => {
        const errorMessages = Object.values(errors).join(', ')
        app.setFlash({
          type: 'wrong',
          message: `Failed to save workflow: ${errorMessages}`,
          time: 3000,
        })
      })

      if (handled.success && handled.data) {
        const savedWorkflow = handled.data.workflow

        if (savedWorkflow) {
          // Get currently selected node ID to preserve selection
          const currentSelectedNodeId = nodesUI.find((node) => node.selected)?.id

          // Sync UI state with server response
          const { nodesUI: updatedNodesUI, edgesUI: updatedEdgesUI } = mapGraphQLToUI(
            savedWorkflow.workflowNodes,
            savedWorkflow.workflowEdges,
            workflowManifest.handlers ?? [],
            nodeDecorator
          )

          // Preserve selection after save
          syncFromServer(updatedNodesUI, updatedEdgesUI, currentSelectedNodeId)
        }

        app.setFlash({
          type: 'correct',
          message: i18n.t('common.saved_successfully'),
          time: 3000,
        })
      }
    } catch (mutationError) {
      app.setFlash({
        type: 'wrong',
        message: `Failed to save workflow: ${mutationError}`,
        time: 3000,
      })
    }
  }, [
    workflow,
    getSerializedNodesHandler,
    getSerializedEdgesHandler,
    saveWorkflowGraph,
    nodeDecorator,
    workflowManifest.handlers,
    syncFromServer,
  ])
  // Custom modal for in-app navigation confirmation
  const { NavConfirmModal, setNavigationBlocked } = useWorkflowNavConfirmModal()

  // Update WorkflowProvider's public state (if provider exists)
  // This syncs the external store when isDirty or handleSave changes
  const updateWorkflowPublicState = useWorkflowUpdate()
  useEffect(() => {
    updateWorkflowPublicState?.({
      isDirty,
      saveWorkflow: handleSave,
      getSerializedNodes: getSerializedNodesHandler,
      getSerializedEdges: getSerializedEdgesHandler,
    })
  }, [
    updateWorkflowPublicState,
    isDirty,
    handleSave,
    getSerializedNodesHandler,
    getSerializedEdgesHandler,
  ])

  // Block navigation when there are unsaved changes
  useEffect(() => {
    setNavigationBlocked(isDirty || sidepanelDirty)
  }, [isDirty, sidepanelDirty, setNavigationBlocked])

  const selectedNode = useMemo(() => nodesUI.find((node) => node.selected), [nodesUI])

  const fixedWidth = 375
  const { onSizeNode, isLaidOut, relayout } = useDagreAutoLayout(
    nodesUI,
    edgesUI,
    setNodesInternal,
    {
      rankdir: 'TB',
      nodeWidth: fixedWidth,
      defaultNodeHeight: 90,
      nodesep: 60,
      ranksep: 60,
    }
  )

  // Relayout when a new connection is created
  useEffect(relayout, [newConnection, edgesUI])

  // Update nodes with validation and step calculation on edges change
  useEffect(() => {
    setNodesInternal((snapshot) => {
      const updated = updateNodesWithValidationAndSteps(snapshot, edgesUI)

      if (JSON.stringify(updated) === JSON.stringify(snapshot)) {
        return snapshot
      }
      return updated
    })
  }, [edgesUI, setNodesInternal])

  // Container-level auto-selection: when new nodes are added, select the first new one
  // to trigger the sidepanel
  // There is some additional selection logic that is being missed when trying to trigger
  // the sidepanel from the FlowNodeAddButton.tsx component. Instead of resolving that bug
  // (which has been painful to find), we just circumvent the issue by triggering selection
  // at this level.
  useEffect(() => {
    const currentNodeIds = new Set(nodesUI.map((n) => n.id))

    // Find nodes that exist in current but not in previous (i.e., newly added nodes)
    const newNodeIds = nodesUI.filter((n) => !previousNodeIds.has(n.id)).map((n) => n.id)

    if (newNodeIds.length > 0) {
      // New nodes were added - select the first new node
      const firstNewNodeId = newNodeIds[0]
      setNodesInternal((snapshot) =>
        snapshot.map((n) => ({
          ...n,
          selected: n.id === firstNewNodeId,
        }))
      )
      setPreviousNodeIds(currentNodeIds)
    } else if (currentNodeIds.size !== previousNodeIds.size) {
      // Node count changed (nodes were removed or other changes), update tracking
      setPreviousNodeIds(currentNodeIds)
    }
  }, [nodesUI, previousNodeIds])

  const onNodesChange = useCallback(
    (changes: NodeChange<NodeUI>[], checkPendingChanges = true): void => {
      // Prevent React Flow from automatically toggling selection state on
      // specific node types (task, approvals, condition).

      const onlySelectionWasChanged = () => {
        // only proceed if there is only one `select` change
        const change = changes.at(0)
        if (changes.length == 1 && change && change.type === 'select') {
          const updatedNode = nodesUI.find((n) => n.id === change.id)
          if (
            updatedNode?.type === 'task' ||
            updatedNode?.type === 'approvals' ||
            updatedNode?.type === 'condition'
          ) {
            return change.selected == false
          }
        }
      }

      if (checkPendingChanges && onlySelectionWasChanged() && sidepanelDirty) {
        setNavigationBlocked(true)
        setPendingSidePanelChangesModal(changes)
        return
      }

      setNavigationBlocked(isDirty)
      setNodesInternal((nodesSnapshot) => applyNodeChanges(changes, nodesSnapshot))
    },
    [nodesUI, sidepanelDirty]
  )
  const onEdgesChange = useCallback((changes: EdgeChange<EdgeUI>[]): void => {
    setEdgesInternal((edgesSnapshot) => applyEdgeChanges(changes, edgesSnapshot))
  }, [])
  const onConnect = useCallback((params: Connection): void => {
    setNewConnection(params)
    setEdgesUI((edgesSnapshot) => {
      // Remove any existing connections that conflict with the new connection
      const filteredEdges = edgesSnapshot.filter((edge) => {
        // Remove edges that connect TO the same target node (to prevent multiple inputs)
        const connectsToSameTarget = edge.target === params.target

        return !connectsToSameTarget
      })

      // Add the new connection
      return addEdge(params, filteredEdges)
    })
  }, [])

  const onNodeClick = useCallback(
    (_event: React.MouseEvent, node: NodeUI): void => {
      let selectionId = node.id

      // If a condition branch node is clicked, select its parent condition instead
      if (isNodeUIConditionIfOrElse(node)) {
        const edges = getEdges()
        const nodes = getNodes()
        const parentEdge = edges.find((e) => e.target === node.id)
        if (parentEdge) {
          const parentNode = nodes.find((n) => n.id === parentEdge.source)
          if (isNodeUICondition(parentNode)) {
            selectionId = parentNode.id
          }
        }
      }

      setNodesInternal((nodesSnapshot) =>
        nodesSnapshot.map((n) => ({
          ...n,
          selected: n.id === selectionId,
        }))
      )
    },
    [getEdges, getNodes]
  )

  // Validation for new connections to prevent cycles and self-loops
  const isValidConnection = useCallback(
    (connection: Connection | EdgeUI): boolean => {
      const currentNodes = getNodes()
      const currentEdges = getEdges()

      const target = currentNodes.find((node) => node.id === connection.target)
      const hasCycle = (node: NodeUI, visited = new Set()) => {
        if (visited.has(node.id)) return false

        visited.add(node.id)

        for (const outgoer of getOutgoers(node, currentNodes, currentEdges)) {
          if (outgoer.id === connection.source) return true
          if (hasCycle(outgoer, visited)) return true
        }
      }

      if (target === undefined || target.id === connection.source) return false

      return !hasCycle(target)
    },
    [getNodes, getEdges]
  )

  // Memoize context value to prevent unnecessary re-renders of consumers
  const contextValue = useMemo(
    () => ({
      buildingBlocks,
      nodesUI,
      setNodesUI,
      edgesUI,
      setEdgesUI,
      isDirty,
      elevateNodeZIndex,
      restoreNodeZIndex,
      triggerConfig: trigger,
      workflowManifest,
      workflow,
      handlersConfig: handlers,
      menuControls,
      sidepanelDirty,
      setSidepanelDirty,
      variablesConfig: variables,
      entityResolution,
    }),
    [
      buildingBlocks,
      nodesUI,
      setNodesUI,
      edgesUI,
      setEdgesUI,
      isDirty,
      elevateNodeZIndex,
      restoreNodeZIndex,
      trigger,
      workflowManifest,
      handlers,
      menuControls,
      sidepanelDirty,
      setSidepanelDirty,
      variables,
      entityResolution,
    ]
  )

  return (
    <WorkflowContext.Provider value={contextValue}>
      <WorkflowLayoutContext.Provider value={{ onSizeNode, isLaidOut, relayout, fixedWidth }}>
        <Box
          style={{ height: `calc(100vh - ${heightOffset})`, position: 'relative' }}
          overflowY="hidden"
          overflowX="hidden"
        >
          <ReactFlow
            nodes={nodesUI}
            edges={edgesUI}
            onNodesChange={onNodesChange}
            onEdgesChange={onEdgesChange}
            onConnect={onConnect}
            onNodeClick={onNodeClick}
            connectionLineComponent={ConnectionLine}
            nodesDraggable={false}
            nodeTypes={nodeTypes}
            edgeTypes={edgeTypes}
            defaultEdgeOptions={{ type: 'default', selectable: false }}
            onlyRenderVisibleElements={false}
            isValidConnection={isValidConnection}
            maxZoom={1.5}
            panOnDrag={!testMode}
            zoomOnScroll={!testMode}
            zoomOnPinch={!testMode}
            proOptions={{ account: 'paid-pro', hideAttribution: true }}
          >
            <Background bgColor="#F9F9FB" color="#BFBFBF" />
            <Sidepanel
              showBuildingBlock={false}
              variablesConfig={variables}
              buildingBlocks={buildingBlocks}
              selectedNode={selectedNode}
            />
            <WorkflowZoom />
          </ReactFlow>
        </Box>
        <NavConfirmModal />
        <WorkflowNavConfirmModal
          open={pendingSidePanelChangesModal !== null}
          onClose={() => {}}
          onSaveChanges={continueEditingSidePanel}
          onDiscard={discardUnsavedSidePanelChanges}
          saveChangesLabel={i18n.t('workflows.nav_confirm_modal.continue_editing')}
        />
      </WorkflowLayoutContext.Provider>
    </WorkflowContext.Provider>
  )
}

const WorkflowContent = (props: Props) => {
  const workflowManifest = props.workflow.workflowManifest

  if (!workflowManifest) {
    return null
  }

  return <WorkflowContentInner {...props} workflowManifest={workflowManifest} />
}

export const WorkflowContainer = (props: Props) => {
  return (
    <ReactFlowProvider>
      <WorkflowContent key={props.workflow.id} {...props} />
    </ReactFlowProvider>
  )
}
